import { $t } from '@/i18n';
import { useEnvOptions } from '@/workbench/components/preview-button/useTagEnvOptions';
import { ENV_TAG_SEPARATOR } from '@/workbench/pages/deployment/publish-setting/components/useEnvTag';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import classNames from 'classnames';
import { isMobileOrNarrowScreen } from '@/utils/screen';
import styles from './tag-env-select.module.scss';
/** 环境数量超过该值时展示搜索框 */
const SEARCH_INPUT_VISIBLE_THRESHOLD = 5;
const SEARCH_FIELDS = [
    o => o.content,
    o => o.env?.name,
    o => o.env?.env,
];
function isLeafMatched(option, keywordLower) {
    return SEARCH_FIELDS.some(getter => (getter(option) ?? '').toString().toLowerCase()
        .includes(keywordLower));
}
function filterOptionsTree(opts, keyword) {
    const k = keyword.trim().toLowerCase();
    if (!k) {
        return opts;
    }
    const result = [];
    for (const option of opts) {
        if (option.children?.length) {
            const filtered = filterOptionsTree(option.children, k);
            if (filtered.length) {
                result.push({ ...option, children: filtered });
            }
        }
        else if (isLeafMatched(option, k)) {
            result.push(option);
        }
    }
    return result;
}
export const TagEnvSelect = defineComponent({
    name: 'RunTimeTagEnvSelect',
    props: {
        envList: {
            type: Array,
            required: true,
        },
        theme: {
            type: String,
        },
        env: {
            type: Object,
        },
        visible: {
            type: Boolean,
        },
    },
    emits: {
        change: (_env) => void 0,
        'update:visible': (_visible) => void 0,
    },
    setup(props, { emit }) {
        const { options, hasTagEnv, taggedEnvs, nonTaggedEnvs, envToOption } = useEnvOptions(() => props.envList);
        const searchKeyword = ref('');
        const isMobile = computed(isMobileOrNarrowScreen);
        const filteredOptions = computed(() => {
            if (isMobile.value) {
                return options.value;
            }
            return filterOptionsTree(options.value, searchKeyword.value);
        });
        const envPaths = computed(() => {
            const items = (props.env?.tag?.split(ENV_TAG_SEPARATOR) ?? []).filter(Boolean);
            const paths = [];
            if (items.length) {
                paths.push(items.reduce((prev, cur) => {
                    paths.push(prev);
                    const merged = [prev, cur].join(ENV_TAG_SEPARATOR);
                    return merged;
                }));
            }
            return paths;
        });
        const resetCls = computed(() => classNames(styles.styleReset, props.theme));
        const overlayClassName = computed(() => classNames(`env-select-popover ${props.theme} ${resetCls.value}`, styles.popover, {
            [styles.hasTag]: hasTagEnv.value,
        }));
        const isSelected = (option) => {
            const { env } = props;
            if (!env?.env) {
                return false;
            }
            const isTarget = !option.children?.length && option.env?.env === env?.env;
            const isPath = option.children?.length && envPaths.value.includes(option.value);
            return Boolean(isTarget || isPath);
        };
        const getDataActive = (option) => +isSelected(option);
        const renderFolder = (option) => (<a-menu data-active={getDataActive(option)} theme={props.theme}>
        <a-sub-menu popupClassName={classNames(resetCls.value)} key={option.value} title={option.content}>
          {option.children?.map(renderSubMenu)}
        </a-sub-menu>
      </a-menu>);
        const renderItem = (option) => (<p onClick={() => emit('change', option.env)} data-active={getDataActive(option)} class={classNames('env-select_options', {
                ['env-select_options-active']: isSelected(option),
            })} key={option.value}>
        {option.content}
      </p>);
        const renderSubMenu = (option) => {
            if (option.children?.length) {
                return (<a-sub-menu data-active={getDataActive(option)} popupClassName={classNames(resetCls.value)} key={option.value} title={option.content}>
            {option.children.map(renderSubMenu)}
          </a-sub-menu>);
            }
            return <a-menu-item>{renderItem(option)}</a-menu-item>;
        };
        const distributeContentRender = (option) => {
            if (option.children?.length) {
                return renderFolder(option);
            }
            return renderItem(option);
        };
        const contentRender = () => {
            if (isMobile.value) {
                return nonTaggedEnvs.value.concat(taggedEnvs.value).map(env => renderItem(envToOption(env)));
            }
            const list = filteredOptions.value;
            return (<div class={styles.desktopContent}>
          {props.envList.length > SEARCH_INPUT_VISIBLE_THRESHOLD ? (<div class={styles.searchInput} onClick={(e) => e.stopPropagation()}>
              <a-input value={searchKeyword.value} placeholder={$t('k_f2c81f4b')} allowClear onChange={(e) => {
                        searchKeyword.value = e.target?.value ?? '';
                    }}>
                <a-icon slot="prefix" type="search"/>
              </a-input>
            </div>) : null}
          {list.length === 0 ? (<div class={styles.emptyTip}>{$t('k_8c98ac18')}</div>) : (list.map(distributeContentRender))}
        </div>);
        };
        const innerVisible = computed({
            get() {
                return props.visible;
            },
            set(val) {
                emit('update:visible', val);
            },
        });
        watch(() => props.visible, (visible) => {
            if (!visible) {
                searchKeyword.value = '';
            }
        });
        return { options, contentRender, overlayClassName, innerVisible };
    },
    render() {
        return (<a-popover v-model={this.innerVisible} overlay-class-name={classNames(this.overlayClassName)} trigger="click" scopedSlots={{
                content: this.contentRender,
            }}>
        {this.$slots.default}
      </a-popover>);
    },
});
